import DoneRoundedIcon from "@mui/icons-material/DoneRounded";

import { useAuth0 } from "@auth0/auth0-react";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import {
  controlKeys,
  detectionKeys,
  entityKeys,
  stepKeys,
} from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { DetectionStatus } from "../../../../utils/constants";
import {
  getEntityTypeAndUniqueIdFromEntityId,
  getEntityTypeFromEntityId,
  isComplexEntityType,
} from "../../../../utils/utils";
import { useEntities } from "../../../stack/environment/hooks/useEntities";
import { icons } from "../../../stack/environment/icons/iconExport";
import IndividualEntityChip from "../../controls/components/EntitiesSection/IndividualEntityChip";
import EvidenceModal from "./EvidenceModal";
import NextStepsSection from "./NextStepsSection";

const DetectionDetails = ({
  detection,
  handleMoveToControl = null,
  toolTipText = "",
}) => {
  const colors = useColors();
  const [openEvidence, setOpenEvidence] = useState(false);
  const { user } = useAuth0();
  const navigate = useNavigate();
  const [updateResolvedIsLoading, setUpdateResolvedIsLoading] = useState(false);

  const { fetchEntityById, resolveDetection, trackEvent } = useAPI();
  const { flattenEntity } = useEntities();
  const queryClient = useQueryClient();

  // Get the first (and only) entity ID from the detection
  const entityId = detection.detected_entity_ids?.[0];

  const isSimpleEntity =
    entityId &&
    entityId.includes("-") &&
    !isComplexEntityType(getEntityTypeFromEntityId(entityId));

  // Create simple entity object directly if it's a simple entity
  const simpleEntity = isSimpleEntity
    ? (() => {
        const [entityType, displayName] =
          getEntityTypeAndUniqueIdFromEntityId(entityId);
        return {
          entity_id: entityId,
          entity_type: entityType,
          display_name: displayName,
          original_unique_id: displayName,
        };
      })()
    : null;

  const entityQuery = useQuery({
    queryKey: entityKeys.entity(entityId),
    queryFn: () => fetchEntityById(entityId),
    select: (response) => {
      if (!response?.data || Object.keys(response.data).length === 0)
        return null;
      return response?.data;
    },
    enabled: !!entityId && !isSimpleEntity, // Only fetch if it's not a simple entity
  });

  const handleResolve = async () => {
    setUpdateResolvedIsLoading(true);
    await resolveDetection(
      detection.controlid_date_detectionid,
      user.given_name,
      user.email
    ).then(() => {
      Toast.success("Detection resolved successfully");
      queryClient.invalidateQueries({ queryKey: detectionKeys.all });
      queryClient.invalidateQueries({ queryKey: controlKeys.all });
      queryClient.invalidateQueries({ queryKey: stepKeys.all });
      setUpdateResolvedIsLoading(false);
    });
  };

  const navigateToControl = () => {
    const message = "User navigated to control from detection tab";
    trackEvent(message).then();
    navigate("/posture/controls?control_id=" + detection.control_id);
  };

  // Use simple entity if available, otherwise use fetched entity
  const finalEntity =
    simpleEntity || (entityQuery.isSuccess && flattenEntity(entityQuery.data));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Typography variant="caption">{detection.description}</Typography>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
        }}
      >
        <CustomToolTip title="click to view evidence">
          <IconButton
            disableRipple
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.text_primary,
              border: "1px solid" + colors.grey_600,
              height: "30px",
              textTransform: "none",
              borderRadius: "10px",
              gap: "8px",
            }}
            onClick={() => {
              setOpenEvidence(true);
            }}
          >
            <SvgToIcon
              IconLogo={icons.evidenceIcon}
              style={{ color: colors.common_white }}
              width={"18px"}
            />
            <Typography variant="overline">View Evidence</Typography>
          </IconButton>
        </CustomToolTip>

        <IconButton
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.text_primary,
            border: "1px solid" + colors.grey_600,
            height: "30px",
            textTransform: "none",
            borderRadius: "10px",
            gap: "8px",
          }}
          onClick={() => {
            return handleMoveToControl
              ? handleMoveToControl()
              : navigateToControl();
          }}
        >
          <Typography variant="overline">View Related Control</Typography>
          <EastRoundedIcon
            sx={{
              width: "16px",
            }}
          />
        </IconButton>
        {detection.status === DetectionStatus.RESOLVED ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.primary_main,
              height: "30px",
              textTransform: "none",
              borderRadius: "10px",
              borderColor: colors.primary_main,
              gap: "8px",
              padding: "0px 8px",
            }}
          >
            <Typography variant="overline">Event Resolved</Typography>
            <CustomToolTip
              title={<Typography variant="caption">{toolTipText}</Typography>}
            >
              <InfoIcon
                sx={{
                  color: colors.primary_24percent,
                  width: "18px",
                }}
              />
            </CustomToolTip>
          </Box>
        ) : (
          <IconButton
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.primary_main,
              border: "1px solid" + colors.grey_600,
              height: "30px",
              textTransform: "none",
              borderRadius: "10px",
              borderColor: colors.primary_main,
              gap: "8px",
            }}
            onClick={handleResolve}
            disabled={updateResolvedIsLoading}
          >
            <Typography variant="overline">Resolve the Event</Typography>
            {updateResolvedIsLoading ? (
              <LoadingIcon size={10} />
            ) : (
              <DoneRoundedIcon
                sx={{
                  width: "16px",
                }}
              />
            )}
          </IconButton>
        )}
      </Box>
      <Divider flexItem style={{ marginTop: "10px" }} />

      {/* Involved Entity Section */}
      {entityQuery.isLoading ? (
        <Box display={"flex"} alignItems={"center"} flexDirection={"column"}>
          <LoadingIcon />
          <Typography variant="subtitle2">
            Loading involved entity...
          </Typography>
        </Box>
      ) : finalEntity ? (
        <Grid container gap={1}>
          <Grid item>
            <Typography variant="subtitle2">Involved Entity</Typography>
          </Grid>
          <Grid container spacing={1}>
            <Grid item>
              <IndividualEntityChip entity={finalEntity} />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid container gap={1}>
          <Grid item>
            <Typography variant="subtitle2">Involved Entity</Typography>
          </Grid>
          <Typography variant="caption">No involved entity found</Typography>
        </Grid>
      )}

      {detection.status !== DetectionStatus.RESOLVED && (
        <NextStepsSection
          detection={detection}
          handleResolve={handleResolve}
          handleMoveToControl={handleMoveToControl}
        />
      )}
      <EvidenceModal
        show={openEvidence}
        handleClose={() => {
          setOpenEvidence(false);
        }}
        evidence={detection.evidence}
      />
    </Box>
  );
};

export default DetectionDetails;
